// Homepage wording carried over from the original landing page.
// Edit the words here; the layout lives in StorySections.jsx.

export const PROBLEM = {
  eyebrow: 'The Problem',
  title: 'Recruiting Changed.',
  accent: 'Your Workload Exploded.',
  lead: 'Transfer portal chaos. NIL bidding wars. Fewer roster spots. Every recruiting decision matters.',
  paragraphs: [
    "You're spending hours on showcases, chasing film, and evaluating players who may never commit—or qualify academically. Meanwhile, the best-fit recruits are getting harder to find.",
  ],
  closer: "You don't need more recruiting hours. You need a better way to find talent.",
};

export const REAL_COST = {
  eyebrow: 'The Real Cost',
  title: "This Isn't Just a Recruiting Problem.",
  accent: "It's an Everything Problem.",
  items: [
    {
      lead: 'Every academic flameout',
      text: "kills your APR and your credibility with admissions. One more and you're losing scholarships.",
    },
    {
      lead: 'Every portal loss',
      text: "signals instability to your AD, your boosters, and the next recruit you're trying to land.",
    },
    {
      lead: 'Every missed evaluation',
      text: 'means a kid you passed on lights it up at a rival program, and everyone notices.',
    },
    {
      lead: 'Every showcase weekend',
      text: "is another dinner missed, another promise broken at home, another step toward a family that's learned to live without you.",
    },
  ],
};

export const WHAT_HASNT_WORKED = {
  eyebrow: "What Hasn't Worked",
  title: "You've Tried",
  accent: 'Everything.',
  subtitle: 'None of it solved the real problem.',
  items: [
    {
      label: 'Showcases',
      quote: "I've been to so many of these damn things. You see a kid throw 92 in a bullpen, then he gets to campus and can't locate a fastball to save his life. It's a crapshoot.",
    },
    {
      label: 'Pipeline Relationships',
      quote: "Junior college coaches are playing everyone against each other. They'll tell you a kid's a lock, then he commits somewhere else two weeks later. Can't trust anyone.",
    },
    {
      label: 'Data Platforms',
      quote: "Players claim they have all the metrics: spin rate, exit velo, launch angle, looks great on paper. Then the kid shows up and has no idea how to compete. Numbers don't tell you if a guy's got a brain or heart.",
    },
    {
      label: 'Academics',
      quote: 'We have our staff vet academics for transfers, and we still got burned. The kid had a 3.2 GPA in high school and was on academic probation by the second semester. High school GPA means nothing anymore.',
    },
    {
      label: 'Transfer Portal',
      quote: "Portal's a band-aid. Half these kids are just looking for the next opportunity. No loyalty. And you have no idea what kind of baggage they're bringing from the last program.",
    },
  ],
};

export const BRIDGE = {
  title: 'What If The Heavy Lifting Was',
  accent: 'Already Done?',
  paragraphs: [
    'What if you opened your laptop and saw a curated list of JUCO players with sortable statistics, easily accessible game film, and their commitment status?',
    'Already screened, academically confirmed, character verified, and skills proven by the coaches who work with them every day?',
  ],
  highlight: 'Sortable Statistics. Easily Accessible Player Video. All JUCO Data in One Place.',
  closing: [
    'No more blind evaluations. No more wasted scholarship slots. No more gambling on projection players who might never develop.',
    'Just a clear, reliable database of players who actually fit your program, delivered to you.',
  ],
  tagline: "That's",
  brand: 'JUCO Pipeline.',
};

export const HOW_IT_WORKS = {
  eyebrow: 'The Process',
  title: 'Spend Less Time Hunting.',
  accent: 'More Time Evaluating.',
  steps: [
    {
      title: 'Search For What You Need',
      text: 'Position, skill set, statistical benchmarks. We filter by what matters to YOUR program, not a generic database search.',
    },
    {
      title: 'We Deliver Vetted Players',
      text: 'Every profile includes up-to-date video, verified statistics, bio information, and more to help you evaluate talent.',
    },
    {
      title: 'You Evaluate And Decide',
      text: "You're still making the final call. We make sure the options in front of you are actually worth your time. Your eyes. Your gut. Better candidates.",
    },
  ],
};

export const FEATURES = {
  eyebrow: 'Player Profile Features',
  title: 'Everything You Need.',
  accent: "Nothing You Don't.",
  items: [
    {
      title: 'Academic Performance',
      text: 'Players can upload academic information, including GPA, unofficial transcripts, and NCAA eligibility. Know before you offer, not after they\'re on campus.',
    },
    {
      title: 'Up-To-Date Video',
      text: 'Game film. Evaluate how players compete in real games, not how they look in a controlled showcase.',
    },
    {
      title: 'Sortable Statistics',
      text: 'Filter and compare players by the metrics that actually matter, verified by Presto Sports.',
    },
    {
      title: 'Uncommitted Tracker',
      text: 'Real-time commitment status on every player. Stop wasting evaluation hours chasing players who are already gone.',
    },
    {
      title: 'Advanced Sorting',
      text: 'Your program. Your criteria. We deliver players who match by position, skill set, academic profile, and cultural fit, not a generic list you have to dig through.',
    },
  ],
};

export const TRANSFORMATION = {
  eyebrow: 'The Transformation',
  title: 'Imagine This',
  accent: 'Instead',
  items: [
    {
      // Hidden from the page for now; delete this line to bring it back.
      hidden: true,
      title: 'Your Inbox',
      text: 'A curated shortlist of pre-vetted players who actually fit. Academics confirmed, character verified, skills proven. Monday morning. Ready to review.',
    },
    {
      title: 'Your APR',
      text: 'Bulletproof. Admissions loves working with you. Your AD stops hovering. Every kid you bring in can actually succeed in the classroom without being babysat.',
    },
    {
      title: 'Your Reputation',
      text: 'Rival coaches are quietly asking what you changed. Boosters bragging about your program at golf outings. Your AD showcases your recruiting in donor meetings, not building a file.',
    },
    {
      title: 'Your Home',
      text: "Dinner with your family. Your daughter's games. Present, really present, because recruiting just got easier.",
    },
    {
      title: 'Your Program',
      text: "Continuity. Culture. Loyal players who develop into leaders and buy in. You're building something that lasts, not plugging holes every summer.",
    },
    {
      title: 'Your Identity',
      text: 'You remember why you fell in love with coaching. And for the first time in years, it feels like the job you actually signed up for.',
    },
  ],
};

export const FAQ = {
  eyebrow: 'Straight Answers',
  title: 'Frequently Asked',
  accent: 'Questions',
  items: [
    {
      question: 'How do I know that this data is verified?',
      answer: "Every player is verified through multiple touchpoints: academic records reviewed directly, performance metrics validated by neutral third parties, and character references provided by the JUCO coaches who worked with them daily, not parents, not travel ball coaches, not the players themselves. If a player doesn't meet our standards, they don't make it into the system. Period.",
    },
    {
      question: "I need to see kids play. I can't recruit off a profile.",
      answer: "You shouldn't, and we'd never ask you to. JUCO Pipeline doesn't replace your evaluation. It replaces the 30 hours of searching it takes to find someone worth evaluating. Every profile includes game video so you can watch players compete in real situations. You're still making the final call. We just make sure the options in front of you are worth your time.",
    },
    {
      question: 'How deep is the talent at the JUCO level?',
      answer: "That's what everyone assumes, and it's exactly why they miss. There are over 400 JUCO programs across the country, and the vast majority of their rosters never get proper exposure to four-year coaches. The best-kept secret in college baseball isn't at a showcase. It's at a JUCO that no one's watching. We watch them all.",
    },
    {
      question: "I don't have the budget for another recruiting tool.",
      answer: "You're already spending thousands on showcase travel, video subscriptions, and evaluation time that leads nowhere. JUCO Pipeline doesn't add to that; it replaces the most expensive and least efficient parts of your current process. The question isn't whether you can afford this. It's whether you can afford to keep doing what you're doing.",
    },
    {
      question: "I don't have time to learn another platform. I'm already drowning.",
      answer: "Good, because there's nothing to learn. No software to install. No dashboards to configure. No learning curve. We deliver vetted player profiles directly to you based on your criteria. If you can read an email, you can use JUCO Pipeline.",
    },
  ],
};

export const URGENCY = {
  eyebrow: 'Time-Sensitive',
  title: 'Roster Limits',
  accent: 'Are Shrinking',
  paragraphs: [
    "Rosters are shrinking. The margin for error is gone. Programs that haven't secured their JUCO pipeline are already behind.",
    'Every week you wait is another week other coaches are building relationships with the players your program needs.',
  ],
  closer: "This isn't next year's problem. This is this recruiting cycle's emergency.",
};

export const CLOSING_CTA = {
  title: 'Smarter Scouting',
  accent: 'Starts Here',
  text: 'Stop grinding. Stop gambling. Stop drowning in dead-end evaluations. Get back to what matters most — coaching.',
  button: 'START YOUR SEARCH',
  note: 'Fast and easy setup. No learning curve. No new workflows. Just better players to help your team win.',
};
